import { format } from 'date-fns';
import { Sparkles, ThumbsDown, ThumbsUp, User } from 'lucide-react';

import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { cn } from '@apps/web/lib/utils';
import {
    useUser,
    useVoiceCallUserRatings,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type VoiceCallUserRatingDisplayProps = {
    readonly voiceCallId: string;
};

export function VoiceCallUserRatingDisplay({
    voiceCallId,
}: VoiceCallUserRatingDisplayProps) {
    const organizationId = useOrganizationId();
    const { data: user } = useUser({
        reason: 'voice-call-user-rating-display',
    });
    const isAdmin = user?.admin ?? false;

    const { data: ratingResponse, isLoading } = useVoiceCallUserRatings(
        organizationId,
        voiceCallId,
        isAdmin,
        { reason: 'voice-call-user-rating-display' },
    );

    const userRating = ratingResponse?.rating;

    if (isLoading) {
        return null; // Don't show loading state for read-only display
    }

    if (!userRating) {
        return null; // No user rating to display
    }

    const ratingAppearance = {
        perfect: {
            className:
                'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400',
            Icon: Sparkles,
        },
        good: {
            className:
                'bg-green-100 text-green-600 dark:bg-green-950/50 dark:text-green-400',
            Icon: ThumbsUp,
        },
        bad: {
            className:
                'bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-400',
            Icon: ThumbsDown,
        },
    }[userRating.label];
    const RatingIcon = ratingAppearance.Icon;

    return (
        <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <div className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-xs">
                <User className="h-3 w-3" />
                <span className="font-medium">
                    {userRating.created_by_user_name || 'User'}
                </span>
                <span>rated this call</span>
                <span className="ml-auto">
                    {format(new Date(userRating.created_at), 'MMM d, h:mm a')}
                </span>
            </div>
            <div className="flex items-start gap-2">
                <div
                    className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-full',
                        ratingAppearance.className,
                    )}
                >
                    <RatingIcon className="h-3.5 w-3.5 fill-current" />
                </div>
                {userRating.comment && (
                    <div className="flex-1">
                        <p className="text-foreground/80 text-sm">
                            {userRating.comment}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
